/**
 * streamMeter — level/bars for a MediaStream the caller ALREADY owns.
 *
 * WHY THIS EXISTS (owner ruling 2026-07-29, blocker 3): the Pipeline B hook used to call
 * startMic(), which opens its own getUserMedia. With the microphone now negotiated as an
 * RTP track, that meant TWO capture streams on ONE microphone for the length of a
 * session — two AEC instances competing on the same device, for a level meter. This
 * attaches the same PCM worklet to the stream that is already being sent, so there is
 * exactly one capture and one AEC instance.
 *
 * It reads only. It never opens a device, never sends audio anywhere, and carries no
 * transport duty — the RTP track is the only path the owner's voice takes to the box.
 *
 * audioStream.ts is deliberately NOT modified beyond exporting the worklet source:
 * Pipeline A (usePTT → startMic) shares that module and must stay byte-identical.
 */
import { WORKLET_SRC } from './audioStream';

export type MeterSample = { level: number; bars: number[] };
export type MeterListener = (sample: MeterSample) => void;

let ctx: AudioContext | null = null;
let source: MediaStreamAudioSourceNode | null = null;
let node: AudioWorkletNode | null = null;
let workletUrl: string | null = null;
let listener: MeterListener | null = null;

export async function startStreamMeter(stream: MediaStream, onSample: MeterListener): Promise<void> {
  if (node) return;                       // already metering
  listener = onSample;

  const Ctor: typeof AudioContext = window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) throw new Error('audio_context_unavailable');

  const nextCtx = new Ctor();
  if (nextCtx.state === 'suspended') {
    // Surface but never throw: some browsers transition on the first sample. A dead
    // meter must be diagnosable without re-instrumenting.
    await nextCtx.resume().catch((e) => {
      console.warn('[streamMeter] AudioContext.resume() rejected:', e, '— state:', nextCtx.state);
    });
  }

  const blob = new Blob([WORKLET_SRC], { type: 'application/javascript' });
  const nextUrl = URL.createObjectURL(blob);
  await nextCtx.audioWorklet.addModule(nextUrl);

  const nextSource = nextCtx.createMediaStreamSource(stream);
  const nextNode = new AudioWorkletNode(nextCtx, 'rexy-pcm-worklet');
  nextNode.port.onmessage = (event: MessageEvent) => {
    if (!listener) return;
    const data = event.data as { level?: number; bars?: number[] };
    // The worklet also posts pcmBuffer; it is IGNORED here. This meter never moves audio.
    listener({
      level: Math.max(0, Math.min(1, Number(data?.level ?? 0))),
      bars: Array.isArray(data?.bars) ? data.bars.map((v) => Math.max(0, Math.min(1, Number(v) || 0))) : [],
    });
  };
  // Deliberately NOT connected to ctx.destination — metering must never be audible.
  nextSource.connect(nextNode);

  ctx = nextCtx;
  source = nextSource;
  node = nextNode;
  workletUrl = nextUrl;
}

export async function stopStreamMeter(): Promise<void> {
  listener = null;
  try { node?.port.close(); } catch {}
  try { node?.disconnect(); } catch {}
  try { source?.disconnect(); } catch {}
  // The stream itself is NOT stopped here — it belongs to the peer connection, which
  // releases it in closePeer(). Stopping it here would kill the owner's mic mid-call.
  if (workletUrl) { try { URL.revokeObjectURL(workletUrl); } catch {} }
  const dying = ctx;
  ctx = null; source = null; node = null; workletUrl = null;
  if (dying) { try { await dying.close(); } catch {} }
}
